import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/api-error';
import { notifyError } from '@/shared/ui/notify';

const STALE_TIME_MS = 30_000;
const GC_TIME_MS = 5 * 60_000;
const MAX_RETRIES = 2;

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

/** 4xx não são repetidos; 5xx e falhas de rede até 2 vezes. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (!(error instanceof ApiError)) return false;
  return error.kind === 'network' || error.kind === 'timeout' || error.status >= 500;
}

function shouldNotify(error: unknown, silent: boolean | undefined): boolean {
  if (silent === true) return false;
  return !(error instanceof ApiError && error.status === 401);
}

/** QueryClient com defaults explícitos e tratamento global de erros (toast com traceId). */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Prefetch de loader (sem observadores) não notifica: a tela notifica ao exibir o erro.
        if (query.getObserversCount() === 0) return;
        if (shouldNotify(error, query.meta?.silent)) notifyError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (shouldNotify(error, mutation.meta?.silent)) notifyError(error);
      },
    }),
    defaultOptions: {
      queries: { staleTime: STALE_TIME_MS, gcTime: GC_TIME_MS, retry: shouldRetryQuery },
      mutations: { retry: false },
    },
  });
}
